import type { Bounds, Position, Rect, Size } from '@/types/geometry';
import { clamp } from './clamp';

export function pointInRect(point: Position, rect: Rect): boolean {
  return (
    point.x >= rect.x &&
    point.x <= rect.x + rect.width &&
    point.y >= rect.y &&
    point.y <= rect.y + rect.height
  );
}

export function rectsIntersect(a: Rect, b: Rect): boolean {
  return !(
    a.x + a.width < b.x ||
    b.x + b.width < a.x ||
    a.y + a.height < b.y ||
    b.y + b.height < a.y
  );
}

/**
 * Clamp a rect inside bounds. Preserves size when possible; shrinks only if the
 * rect is larger than the bounds themselves.
 */
export function clampRectToBounds(rect: Rect, bounds: Bounds): Rect {
  const boundsWidth = bounds.maxX - bounds.minX;
  const boundsHeight = bounds.maxY - bounds.minY;

  const width = Math.min(rect.width, boundsWidth);
  const height = Math.min(rect.height, boundsHeight);

  const x = clamp(rect.x, bounds.minX, bounds.maxX - width);
  const y = clamp(rect.y, bounds.minY, bounds.maxY - height);

  return { x, y, width, height };
}

export function normalizeDragRect(start: Position, current: Position): Rect {
  const x = Math.min(start.x, current.x);
  const y = Math.min(start.y, current.y);
  const width = Math.abs(current.x - start.x);
  const height = Math.abs(current.y - start.y);
  return { x, y, width, height };
}

export function sizeSatisfies(size: Size, minSize: Size): boolean {
  return size.width >= minSize.width && size.height >= minSize.height;
}

export function domRectToRect(r: DOMRect): Rect {
  return { x: r.x, y: r.y, width: r.width, height: r.height };
}
